import { withFormik } from "formik";
import * as Yup from "yup";

const namePattern = /^[A-Za-z0-9 ]+$/;

export const getStoredSetupIntentId = (registrationData) => {
  const setupIntent = registrationData?.stripeSetupIntent;
  return (
    registrationData?.stripeSetupIntentId ??
    setupIntent?.setup_intent_id ??
    setupIntent?.id ??
    setupIntent?.setupIntentId ??
    null
  );
};

export const getStoredSetupIntentClientSecret = (registrationData) =>
  registrationData?.stripeSetupIntentClientSecret ??
  registrationData?.stripeSetupIntent?.client_secret ??
  registrationData?.stripeSetupIntent?.clientSecret ??
  null;

export const extractSetupIntentId = (setupIntentData) =>
  setupIntentData?.setup_intent_id ??
  setupIntentData?.id ??
  setupIntentData?.setupIntentId ??
  null;

export const extractSetupIntentClientSecret = (setupIntentData) =>
  setupIntentData?.client_secret ?? setupIntentData?.clientSecret ?? null;

export const extractPublishableKey = (tokenRes) => {
  const data = tokenRes?.data ?? tokenRes;
  return (
    data?.stripe_publishable_key ??
    data?.publishable_key ??
    data?.publishable_token ??
    tokenRes?.stripe_publishable_key ??
    tokenRes?.publishable_key ??
    tokenRes?.publishable_token ??
    null
  );
};

export const isStripeApiSuccess = (res) =>
  res?.status === true || res?.status === 1 || res?.status === "1";

const pickFirstDefined = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }
  return null;
};

const toCardListArray = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (!payload || typeof payload !== "object") {
    return [];
  }
  if (Array.isArray(payload.cards)) {
    return payload.cards;
  }
  if (Array.isArray(payload.card_list)) {
    return payload.card_list;
  }
  if (Array.isArray(payload.data)) {
    return payload.data;
  }
  if (payload.card && typeof payload.card === "object") {
    return [payload.card];
  }
  if (
    payload.last4 ||
    payload.last_four ||
    payload.card_last_four ||
    payload.brand ||
    payload.card_brand
  ) {
    return [payload];
  }
  return [];
};

export const normalizeRegistrationCardFromApi = (rawCard = {}) => {
  const card = rawCard?.card && typeof rawCard.card === "object" ? rawCard.card : rawCard;
  const last4 = pickFirstDefined(
    card.last4,
    card.last_four,
    card.card_last_four,
    card.lastFour
  );
  const brand = pickFirstDefined(card.brand, card.card_brand, card.cardBrand);
  const expMonth = pickFirstDefined(
    card.exp_month,
    card.expMonth,
    card.expiry_month,
    card.month
  );
  const expYear = pickFirstDefined(
    card.exp_year,
    card.expYear,
    card.expiry_year,
    card.year
  );
  const cardholderName = pickFirstDefined(
    card.name,
    card.cardholder_name,
    card.card_holder_name,
    card.cardHolderName,
    rawCard.name,
    rawCard.cardholder_name,
    rawCard.card_holder_name
  );
  const billingZipCode = pickFirstDefined(
    card.billing_zip,
    card.billing_zip_code,
    card.billingZipCode,
    card.postal_code,
    card.zip,
    card.zip_code,
    card.zipCode,
    rawCard.billing_zip,
    rawCard.billing_zip_code,
    rawCard.zip_code
  );
  const isDefault = Boolean(
    card.is_default ??
      card.isDefault ??
      card.default ??
      rawCard.is_default ??
      rawCard.isDefault
  );
  const paymentMethodId = pickFirstDefined(
    card.id,
    card.payment_method_id,
    card.paymentMethodId,
    rawCard.id,
    rawCard.payment_method_id,
    rawCard.paymentMethodId
  );

  return {
    id: paymentMethodId ? String(paymentMethodId) : "",
    paymentMethodId: paymentMethodId ? String(paymentMethodId) : "",
    last4: last4 ? String(last4) : "",
    brand: brand ? String(brand).toLowerCase() : "",
    expMonth: expMonth != null ? Number(expMonth) : null,
    expYear: expYear != null ? Number(expYear) : null,
    cardholderName: cardholderName ? String(cardholderName) : "",
    billingZipCode: billingZipCode
      ? String(billingZipCode).replace(/\D/g, "").slice(0, 5)
      : "",
    isDefault,
  };
};

export const extractPaymentMethodIdFromSetupIntent = (setupIntent) => {
  const paymentMethod = setupIntent?.payment_method;
  if (typeof paymentMethod === "string" && paymentMethod) {
    return paymentMethod;
  }
  if (paymentMethod && typeof paymentMethod === "object" && paymentMethod.id) {
    return String(paymentMethod.id);
  }
  return null;
};

export const formatSavedCardBrandLabel = (brand, t) => {
  const normalized = String(brand || "").toLowerCase();
  const labels = {
    visa: "Visa",
    mastercard: "Mastercard",
    amex: "American Express",
    discover: "Discover",
  };
  if (labels[normalized]) {
    return labels[normalized];
  }
  if (!normalized) {
    return t("NUTRITION_CARD_PREVIEW_LBL_BRAND");
  }
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

export const formatSavedCardExpiryLabel = (expMonth, expYear) => {
  if (expMonth == null || expYear == null) {
    return "";
  }
  const mm = String(expMonth).padStart(2, "0");
  const year = String(expYear);
  const yyyy = year.length === 2 ? `20${year}` : year;
  return `${mm}/${yyyy}`;
};

export const extractRegistrationCardsFromListResponse = (response) => {
  const payload = response?.data ?? response ?? {};
  return toCardListArray(payload)
    .map(normalizeRegistrationCardFromApi)
    .filter((card) => card.last4 || card.brand || card.cardholderName);
};

export const pickDefaultRegistrationCard = (cards = []) => {
  if (!Array.isArray(cards) || cards.length === 0) {
    return null;
  }
  return cards.find((card) => card.isDefault) || cards[0];
};

/** Fallback copy when i18n keys are not yet in the translation API */
export const cardOnFileValidationMessageFallbacks = {
  NUTRITION_CARD_LBL_PAYMENT_METHOD: "Payment Method",
  NUTRITION_CARD_BTN_CHANGE: "Change Card",
  NUTRITION_CARD_BTN_EDIT: "Edit",
  NUTRITION_CARD_BTN_UPDATE: "Update Card",
  NUTRITION_CARD_BTN_CANCEL_CHANGE: "Cancel",
  NUTRITION_CARD_LBL_UPDATING: "Updating payment method...",
  NUTRITION_CARD_LBL_EXPIRES_PREFIX: "Expires",
  NUTRITION_CARD_ERR_CHANGE_FAILED:
    "Unable to save the new payment method. Your existing payment method has not been changed.",
  NUTRITION_CARD_ERR_CHANGE_NOT_ALLOWED:
    "Payment method can only be changed during registration.",
  NUTRITION_CARD_MSG_CHANGE_SUCCESS: "Payment method updated successfully.",
};

export const cardOnFileInitialValues = {
  cardholderName: "",
  billingZipCode: "",
  cardNumberElement: null,
  cardExpiryElement: null,
  cardCvcElement: null,
};

const stripeFieldComplete = (value) => value === "valid";

export const cardOnFileValidationSchema = Yup.object().shape({
  cardholderName: Yup.string()
    .trim("SPACE_VALIDATION")
    .strict(true)
    .matches(namePattern, "SPECIAL_CHAR_VALIDATION")
    .min(1, "CARDHOLDER_VAL")
    .required("CARDHOLDER_VAL"),
  cardNumberElement: Yup.mixed()
    .required("NUTRITION_CARD_VAL_CARD_NUMBER_REQUIRED")
    .test(
      "cardNumberComplete",
      "NUTRITION_CARD_VAL_CARD_NUMBER_INVALID",
      stripeFieldComplete
    ),
  cardExpiryElement: Yup.mixed()
    .required("NUTRITION_CARD_VAL_EXPIRY_REQUIRED")
    .test(
      "cardExpiryComplete",
      "NUTRITION_CARD_VAL_EXPIRY_INVALID",
      stripeFieldComplete
    ),
  cardCvcElement: Yup.mixed()
    .required("NUTRITION_CARD_VAL_CVV_REQUIRED")
    .test("cardCvcComplete", "NUTRITION_CARD_VAL_CVV_INVALID", stripeFieldComplete),
  billingZipCode: Yup.string()
    .matches(/^[0-9]+$/, "ONLY_DIGITS_VALIDATION")
    .min(5, "ZIP_FIVE_CHAR_VALIDATION")
    .max(5, "ZIP_FIVE_CHAR_VALIDATION")
    .required("ZIP_CODE_VALIDATION"),
});

export const isCardOnFileFormComplete = (values) => {
  try {
    cardOnFileValidationSchema.validateSync(values, { abortEarly: true });
    return true;
  } catch {
    return false;
  }
};

const formikEnhancer = withFormik({
  validationSchema: cardOnFileValidationSchema,
  mapPropsToValues: () => ({ ...cardOnFileInitialValues }),
  handleSubmit: () => {},
  displayName: "CardOnFileForm",
  enableReinitialize: true,
  validateOnBlur: true,
  validateOnChange: true,
  isInitialValid: false,
});

export default formikEnhancer;
